import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Loader from '../../../Components/Loader/Loader';
import './MyTickets.css';

const TICKETS_API = 'https://pro-customers-api.omyfood.io/api/tickets/my-tickets';

const parseDateLocal = (s) => {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = String(s).split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const dte = new Date(s);
    return Number.isNaN(dte?.getTime?.()) ? null : dte;
};

const money = (n) => {
    const val = parseFloat(n);
    if (!Number.isFinite(val)) return '$0.00';
    return `$${val.toFixed(2)}`;
};

export default function MyTickets({ embed = false }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tickets, setTickets] = useState([]);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchTickets = async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('authToken');
                const res = await axios.get(TICKETS_API, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                        'x-access-token': token || undefined,
                        'Content-Type': 'application/json',
                    },
                    withCredentials: false,
                });
                const list = res?.data?.tickets || res?.data?.data || res?.data || [];
                if (!mounted) return;
                setTickets(Array.isArray(list) ? list : []);
            } catch (err) {
                if (!mounted) return;
                const status = err?.response?.status;
                if (status >= 500) {
                    setError('Failed to load your tickets');
                }
                // eslint-disable-next-line no-console
                console.error('MyTickets fetch error', err);
                setTickets([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchTickets();
        return () => { mounted = false; };
    }, []);

    // no-op: page renders regardless of login; API returns empty list when unauthorized

    return (
        <div className={`mytickets-page ${embed ? 'mytickets-page--embed' : ''}`}>
            {loading && <Loader />}
            <div className="mytickets-container">
                <h1 className="mt-header">My Tickets</h1>
                {error && <div className="mt-error">{error}</div>}
                {(!tickets || tickets.length === 0) && !loading && (
                    <div className="mt-empty">No Reservations Yet</div>
                )}
                <div className="mt-list">
                    {tickets.map((t, idx) => {
                        // Defensive mapping from various possible shapes
                        const eventObj = t.event || t.eventDetails || {};
                        const eventTitle = t.event_title || eventObj.title || t.title || 'Event';
                        const venueRaw = t.venue || eventObj.venue || eventObj.venue_name || eventObj.location || 'Venue TBA';
                        const mapsUrl = venueRaw && venueRaw !== 'Venue TBA' ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueRaw)}` : '';
                        const venue = mapsUrl ? (
                            <a className="map-link" href={mapsUrl} target="_blank" rel="noopener noreferrer">{venueRaw}</a>
                        ) : venueRaw;
                        const start = t.start_time || eventObj.start_time || eventObj.event_datetime || eventObj.start || t.event_datetime;
                        const end = t.end_time || eventObj.end_time || eventObj.end || null;
                        const whenStart = start ? parseDateLocal(start) : null;
                        const whenEnd = end ? parseDateLocal(end) : null;
                        const datePart = whenStart
                            ? whenStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                            : '';
                        const timeRangeFallback = whenStart
                            ? `${whenStart.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}${whenEnd ? ` - ${whenEnd.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : ''}`
                            : '';
                        let goodToKnow = {};
                        try { goodToKnow = JSON.parse(eventObj.good_to_know || '{}'); } catch (_) { goodToKnow = {}; }
                        const timing = (goodToKnow && typeof goodToKnow.timing === 'string') ? goodToKnow.timing.trim() : '';
                        const whenStr = datePart
                            ? `${datePart}${timing ? ` | ${timing}` : (timeRangeFallback ? ` | ${timeRangeFallback}` : '')}`
                            : (timing || timeRangeFallback || '');
                        const totalPaid = t.price_cents ? (parseFloat(t.price_cents) / 100) : (t.total_amount || t.amount || t.totalPaid || t.total_paid || 0);
                        const ticketsArr = Array.isArray(t.tickets) ? t.tickets : (Array.isArray(t.ticket_ids) ? t.ticket_ids : [t.ticket_id].filter(Boolean));
                        const ticketIds = ticketsArr.map((x) => x?.id || x?.ticket_id || x?.code || x).filter(Boolean);
                        const ticketCount = t.quantity || t.qty || t.tickets_count || (ticketIds.length || (t.qty ? t.qty : 1));
                        const status = (t.status || 'Booked').toString();
                        const purchasedOnRaw = t.created_at || t.purchased_at || t.createdAt || t.purchase_time;
                        const purchasedOn = purchasedOnRaw ? parseDateLocal(purchasedOnRaw) : null;
                        const purchasedStr = purchasedOn ? purchasedOn.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

                        const handleView = () => {
                            setSelectedTicket(t);
                            setViewerOpen(true);
                        };
                        // Download button removed per request; keep no-op to avoid reference issues in older markup
                        const handleDownload = () => {};

                        return (
                            <div key={t.id || t.booking_id || idx} className="mt-card">
                                <div className="mt-row">
                                    <div className="mt-col">
                                        <div className="mt-line"><span className="mt-emoji">🎃</span> <strong>Event:</strong> {eventTitle}</div>
                                        <div className="mt-line"><span className="mt-emoji">📍</span> <strong>Venue:</strong> {venue}</div>
                                        {whenStr && <div className="mt-line"><span className="mt-emoji">📅</span> {whenStr}</div>}
                                        <div className="mt-line is-meta">
                                            <span><span className="mt-emoji">💰</span> Total Paid: {money(totalPaid)}</span>
                                            <span className="mt-sep">|</span>
                                            <span><span className="mt-emoji">🎫</span> {ticketCount} Tickets</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-divider" />
                                <div className="mt-line wrap"><strong>Ticket IDs:</strong> {ticketIds.length ? ticketIds.join(' | ') : '—'}</div>
                                <div className="mt-line"><strong>Status:</strong> <span className="mt-status booked">🟢 {status}</span></div>
                                {purchasedStr && <div className="mt-line"><strong>Purchased On:</strong> {purchasedStr}</div>}
                                <div className="mt-actions">
                                    <button className="mt-btn mt-btn-primary" onClick={handleView}>View Tickets & QR Codes 🔍</button>
                                    <button className="mt-btn mt-btn-secondary" onClick={handleDownload}>Download All Tickets 🧾</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {/* Inline ticket viewer modal */}
            {viewerOpen && selectedTicket && (
                <div className="mt-modal-backdrop" onClick={() => { setViewerOpen(false); setSelectedTicket(null); }}>
                    <div className="mt-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="mt-modal-header">
                            <div className="mt-modal-title">Ticket</div>
                            <button className="mt-modal-close" onClick={() => { setViewerOpen(false); setSelectedTicket(null); }} aria-label="Close">×</button>
                        </div>
                        <div className="mt-modal-body">
                            <div className="mt-ticket-title">{selectedTicket?.event?.title || selectedTicket?.event_title || 'Event'}</div>
                            <div className="mt-ticket-row"><strong>Ticket ID:</strong> {selectedTicket?.ticket_id || selectedTicket?.id}</div>
                            <div className="mt-ticket-row"><strong>Status:</strong> {String(selectedTicket?.status || 'Booked')}</div>
                            <div className="mt-ticket-qr">
                                {selectedTicket?.qr_image_url ? (
                                    <img src={selectedTicket.qr_image_url} alt="Ticket QR Code" />
                                ) : (
                                    <div className="mt-qr-missing">QR image not available</div>
                                )}
                            </div>
                        </div>
                        <div className="mt-modal-actions">
                            <button className="mt-btn mt-btn-primary" onClick={() => { setViewerOpen(false); setSelectedTicket(null); }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
